import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  query, 
  where,
  orderBy,
  limit,
  serverTimestamp,
  addDoc,
  arrayUnion,
  arrayRemove,
  deleteDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';

/**
 * Creates a new guide for an item
 * @param {string} itemId - The ID of the item
 * @param {Object} guideData - The guide data
 * @param {Array} imageFiles - Array of image files
 * @returns {Promise<Object>} - The created guide
 */
export const createGuide = async (itemId, guideData, imageFiles = []) => {
  try {
    // Create new guide document
    const newGuideRef = doc(collection(db, "guides"));
    const guideId = newGuideRef.id;
    
    // Upload images first
    const uploadedImages = [];
    for (let i = 0; i < imageFiles.length; i++) {
      const imageFile = imageFiles[i];
      const imageData = guideData.images[i];
      
      if (imageFile && imageData) {
        try {
          // Validate file type
          const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
          if (!validImageTypes.includes(imageFile.type)) {
            throw new Error(`Unsupported file type: ${imageFile.type}. Please upload a JPEG, PNG, GIF, or WebP image.`);
          }
          
          // Validate file size (5MB max)
          const maxSize = 5 * 1024 * 1024; // 5MB
          if (imageFile.size > maxSize) {
            throw new Error(`File is too large. Maximum size is 5MB.`);
          }
          
          // Create filename
          const fileExtension = imageFile.name.split('.').pop().toLowerCase();
          const filename = `guide_${guideId}_${i}.${fileExtension}`;
          
          // Upload to storage
          const storageRef = ref(storage, `guide_images/${filename}`);
          const metadata = {
            contentType: imageFile.type,
            customMetadata: {
              'guideId': guideId,
              'itemId': itemId,
              'imageIndex': i.toString(),
              'altText': imageData.altText || '',
              'uploadTime': new Date().toISOString()
            }
          };
          
          await uploadBytes(storageRef, imageFile, metadata);
          const imageUrl = await getDownloadURL(storageRef);
          
          uploadedImages.push({
            url: imageUrl,
            altText: imageData.altText || '',
            caption: imageData.caption || '',
            order: i
          });
        } catch (error) {
          console.error(`Error uploading image ${i}:`, error);
          throw new Error(`Failed to upload image ${i + 1}: ${error.message}`);
        }
      }
    }
    
    // Create guide document
    const guideToSave = {
      itemId: itemId,
      title: guideData.title,
      content: guideData.content,
      images: uploadedImages,
      hazards: guideData.hazards || [],
      ppe: guideData.ppe || [],
      quickFacts: guideData.quickFacts || {},
      version: 1,
      status: 'draft', // draft or published
      authorId: guideData.authorId,
      authorName: guideData.authorName,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      publishedAt: null,
      publishedBy: null
    };
    
    await setDoc(newGuideRef, guideToSave);
    
    return { id: guideId, ...guideToSave };
  } catch (error) {
    console.error("Error creating guide:", error);
    throw error;
  }
};

/**
 * Updates an existing guide
 * @param {string} guideId - The ID of the guide to update
 * @param {Object} guideData - The updated guide data
 * @param {Array} newImageFiles - Array of new image files
 * @returns {Promise<Object>} - The updated guide
 */
export const updateGuide = async (guideId, guideData, newImageFiles = []) => {
  try {
    const guideRef = doc(db, "guides", guideId);
    const guideSnap = await getDoc(guideRef);
    
    if (!guideSnap.exists()) {
      throw new Error("Guide not found");
    }
    
    const currentGuide = guideSnap.data();
    
    // Handle new image uploads
    const newImages = [];
    let imageIndex = currentGuide.images.length;
    
    for (let i = 0; i < newImageFiles.length; i++) {
      const imageFile = newImageFiles[i];
      const imageData = guideData.newImages[i];
      
      if (imageFile && imageData) {
        try {
          // Validate file type
          const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
          if (!validImageTypes.includes(imageFile.type)) {
            throw new Error(`Unsupported file type: ${imageFile.type}. Please upload a JPEG, PNG, GIF, or WebP image.`);
          }
          
          // Validate file size (5MB max)
          const maxSize = 5 * 1024 * 1024; // 5MB
          if (imageFile.size > maxSize) {
            throw new Error(`File is too large. Maximum size is 5MB.`);
          }
          
          // Create filename
          const fileExtension = imageFile.name.split('.').pop().toLowerCase();
          const filename = `guide_${guideId}_${imageIndex}.${fileExtension}`;
          
          // Upload to storage
          const storageRef = ref(storage, `guide_images/${filename}`);
          const metadata = {
            contentType: imageFile.type,
            customMetadata: {
              'guideId': guideId,
              'itemId': currentGuide.itemId,
              'imageIndex': imageIndex.toString(),
              'altText': imageData.altText || '',
              'uploadTime': new Date().toISOString()
            }
          };
          
          await uploadBytes(storageRef, imageFile, metadata);
          const imageUrl = await getDownloadURL(storageRef);
          
          newImages.push({
            url: imageUrl,
            altText: imageData.altText || '',
            caption: imageData.caption || '',
            order: imageIndex
          });
          
          imageIndex++;
        } catch (error) {
          console.error(`Error uploading new image ${i}:`, error);
          throw new Error(`Failed to upload new image ${i + 1}: ${error.message}`);
        }
      }
    }
    
    // Combine existing and new images
    const allImages = [...(currentGuide.images || []), ...newImages];
    
    // Update guide document
    const updateData = {
      title: guideData.title,
      content: guideData.content,
      images: allImages,
      hazards: guideData.hazards || [],
      ppe: guideData.ppe || [],
      quickFacts: guideData.quickFacts || {},
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(guideRef, updateData);
    
    return { id: guideId, ...currentGuide, ...updateData };
  } catch (error) {
    console.error("Error updating guide:", error);
    throw error;
  }
};

/**
 * Publishes a guide (creates new version)
 * @param {string} guideId - The ID of the guide to publish
 * @param {string} publishedBy - The ID of the user publishing the guide
 * @param {string} publishedByName - The name of the user publishing the guide
 * @returns {Promise<Object>} - The published guide
 */
export const publishGuide = async (guideId, publishedBy, publishedByName) => {
  try {
    console.log('publishGuide called with:', { guideId, publishedBy, publishedByName });

    if (!guideId) {
      throw new Error('Guide ID is required for publishing');
    }

    const guideRef = doc(db, "guides", guideId);
    const guideSnap = await getDoc(guideRef);

    if (!guideSnap.exists()) {
      throw new Error("Guide not found");
    }

    const currentGuide = guideSnap.data();
    console.log('Current guide data:', currentGuide);

    // Determine next version based on existing published guide
    const latestPublished = await getPublishedGuide(currentGuide.itemId);
    const newVersion = ((latestPublished && latestPublished.version) || 0) + 1;
    console.log('New version will be:', newVersion);

    // Archive existing published guide if different from current
    if (latestPublished && latestPublished.id !== guideId) {
      console.log('Archiving previous published version:', latestPublished.id);
      try {
        await archiveGuideVersion(latestPublished.id, latestPublished);
        await updateDoc(doc(db, 'guides', latestPublished.id), {
          status: 'archived',
          updatedAt: new Date().toISOString(),
        });
      } catch (archiveError) {
        console.warn('Failed to archive previous guide version (non-critical):', archiveError);
        // Continue with publishing even if archiving fails
      }
    }

    // Update current guide to published status
    const updateData = {
      status: 'published',
      version: newVersion,
      publishedAt: new Date().toISOString(),
      publishedBy: publishedBy,
      publishedByName: publishedByName,
      updatedAt: new Date().toISOString(),
    };

    console.log('Updating guide with data:', updateData);
    await updateDoc(guideRef, updateData);

    const result = { id: guideId, ...currentGuide, ...updateData };
    console.log('Guide published successfully:', result);
    return result;
  } catch (error) {
    console.error("Error publishing guide:", error);
    throw error;
  }
};

/**
 * Archives a guide version
 * @param {string} guideId - The ID of the guide
 * @param {Object} guideData - The guide data to archive
 */
const archiveGuideVersion = async (guideId, guideData) => {
  try {
    if (!guideId || !guideData) {
      console.warn('Invalid data for archiving:', { guideId, guideData });
      return;
    }
    
    const archiveRef = doc(collection(db, "guide_archives"));
    const archiveData = {
      originalGuideId: guideId,
      itemId: guideData.itemId || '',
      version: guideData.version || 0,
      title: guideData.title || '',
      content: guideData.content || '',
      images: guideData.images || [],
      hazards: guideData.hazards || [],
      ppe: guideData.ppe || [],
      quickFacts: guideData.quickFacts || {},
      publishedAt: guideData.publishedAt || null,
      publishedBy: guideData.publishedBy || null,
      publishedByName: guideData.publishedByName || null,
      archivedAt: new Date().toISOString()
    };
    
    await setDoc(archiveRef, archiveData);
    console.log('Guide version archived successfully');
  } catch (error) {
    console.error("Error archiving guide version:", error);
    // Don't throw error as this is not critical
  }
};

/**
 * Gets the published guide for an item
 * @param {string} itemId - The ID of the item
 * @returns {Promise<Object|null>} - The published guide or null
 */
export const getPublishedGuide = async (itemId) => {
  try {
    console.log('getPublishedGuide called with itemId:', itemId);
    
    // First try the composite index query
    try {
      const guidesQuery = query(
        collection(db, "guides"),
        where("itemId", "==", itemId),
        where("status", "==", "published"),
        orderBy("version", "desc"),
        limit(1)
      );
      
      console.log('Executing query for published guides...');
      const querySnapshot = await getDocs(guidesQuery);
      console.log('Query result - empty:', querySnapshot.empty, 'size:', querySnapshot.size);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const guideData = { id: doc.id, ...doc.data() };
        console.log('Found published guide:', guideData);
        return guideData;
      }
    } catch (indexError) {
      console.log('Composite index not ready, falling back to simple query:', indexError.message);
      
      // Fallback: Get all guides for the item and filter in memory
      const simpleQuery = query(
        collection(db, "guides"),
        where("itemId", "==", itemId)
      );
      
      const querySnapshot = await getDocs(simpleQuery);
      const guides = [];
      
      querySnapshot.forEach((doc) => {
        const guideData = { id: doc.id, ...doc.data() };
        if (guideData.status === 'published') {
          guides.push(guideData);
        }
      });
      
      // Sort by version descending and return the latest
      guides.sort((a, b) => (b.version || 0) - (a.version || 0));
      
      if (guides.length > 0) {
        console.log('Found published guide via fallback:', guides[0]);
        return guides[0];
      }
    }
    
    console.log('No published guide found for itemId:', itemId);
    return null;
  } catch (error) {
    console.error("Error getting published guide:", error);
    throw error;
  }
};

/**
 * Gets the draft guide for an item
 * @param {string} itemId - The ID of the item
 * @returns {Promise<Object|null>} - The draft guide or null
 */
export const getDraftGuide = async (itemId) => {
  try {
    console.log('getDraftGuide called with itemId:', itemId);
    
    // First try the composite index query
    try {
      const guidesQuery = query(
        collection(db, "guides"),
        where("itemId", "==", itemId),
        where("status", "==", "draft"),
        orderBy("updatedAt", "desc"),
        limit(1)
      );
      
      const querySnapshot = await getDocs(guidesQuery);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
    } catch (indexError) {
      console.log('Composite index not ready, falling back to simple query:', indexError.message);
      
      // Fallback: Get all guides for the item and filter in memory
      const simpleQuery = query(
        collection(db, "guides"),
        where("itemId", "==", itemId)
      );
      
      const querySnapshot = await getDocs(simpleQuery);
      const guides = [];
      
      querySnapshot.forEach((doc) => {
        const guideData = { id: doc.id, ...doc.data() };
        if (guideData.status === 'draft') {
          guides.push(guideData);
        }
      });
      
      // Sort by updatedAt descending and return the latest
      guides.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
      
      if (guides.length > 0) {
        return guides[0];
      }
    }
    
    return null;
  } catch (error) {
    console.error("Error getting draft guide:", error);
    throw error;
  }
};

/**
 * Gets all guides for an item (for debugging)
 * @param {string} itemId - The ID of the item
 * @returns {Promise<Array>} - Array of all guides
 */
export const getAllGuidesForItem = async (itemId) => {
  try {
    console.log('getAllGuidesForItem called with itemId:', itemId);
    
    const guidesQuery = query(
      collection(db, "guides"),
      where("itemId", "==", itemId),
      orderBy("updatedAt", "desc")
    );
    
    const querySnapshot = await getDocs(guidesQuery);
    const guides = [];
    
    querySnapshot.forEach((doc) => {
      guides.push({ id: doc.id, ...doc.data() });
    });
    
    console.log('All guides for item:', guides);
    return guides;
  } catch (error) {
    console.error("Error getting all guides for item:", error);
    throw error;
  }
};

/**
 * Gets archived versions of a guide
 * @param {string} guideId - The ID of the guide
 * @returns {Promise<Array>} - Array of archived versions
 */
export const getArchivedVersions = async (guideId) => {
  try {
    const archivesQuery = query(
      collection(db, "guide_archives"),
      where("originalGuideId", "==", guideId),
      orderBy("version", "desc")
    );
    
    const querySnapshot = await getDocs(archivesQuery);
    const archives = [];
    
    querySnapshot.forEach((doc) => {
      archives.push({ id: doc.id, ...doc.data() });
    });
    
    return archives;
  } catch (error) {
    console.error("Error getting archived versions:", error);
    throw error;
  }
};

/**
 * Deletes a guide
 * @param {string} guideId - The ID of the guide to delete
 * @returns {Promise<void>}
 */
export const deleteGuide = async (guideId) => {
  try {
    const guideRef = doc(db, "guides", guideId);
    const guideSnap = await getDoc(guideRef);
    
    if (!guideSnap.exists()) {
      throw new Error("Guide not found");
    }
    
    const guideData = guideSnap.data();
    
    // Delete associated images from storage
    if (guideData.images && guideData.images.length > 0) {
      for (const image of guideData.images) {
        try {
          const imageRef = ref(storage, image.url);
          await deleteObject(imageRef);
        } catch (error) {
          console.error("Error deleting image:", error);
          // Continue with other images
        }
      }
    }
    
    // Delete the guide document
    await deleteDoc(guideRef);
  } catch (error) {
    console.error("Error deleting guide:", error);
    throw error;
  }
};

/**
 * Deletes all draft guides for an item except the specified one
 * @param {string} itemId - The ID of the item
 * @param {string} keepGuideId - The ID of the draft to keep
 * @returns {Promise<void>}
 */
export const deleteOtherDrafts = async (itemId, keepGuideId) => {
  try {
    console.log('Deleting other drafts for item:', itemId, 'keeping:', keepGuideId);
    
    // Get all drafts for this item
    const guidesQuery = query(
      collection(db, "guides"),
      where("itemId", "==", itemId),
      where("status", "==", "draft")
    );
    
    const querySnapshot = await getDocs(guidesQuery);
    const deletePromises = [];
    
    querySnapshot.forEach((doc) => {
      const guideData = doc.data();
      if (doc.id !== keepGuideId) {
        console.log('Deleting draft:', doc.id);
        deletePromises.push(deleteGuide(doc.id));
      }
    });
    
    if (deletePromises.length > 0) {
      await Promise.all(deletePromises);
      console.log('Deleted', deletePromises.length, 'other drafts');
    } else {
      console.log('No other drafts to delete');
    }
  } catch (error) {
    console.error("Error deleting other drafts:", error);
    throw error;
  }
};

/**
 * Deletes ALL draft guides for an item
 * @param {string} itemId - The ID of the item
 * @returns {Promise<void>}
 */
export const deleteAllDrafts = async (itemId) => {
  try {
    console.log('Deleting ALL drafts for item:', itemId);
    
    // Get all drafts for this item
    const guidesQuery = query(
      collection(db, "guides"),
      where("itemId", "==", itemId),
      where("status", "==", "draft")
    );
    
    const querySnapshot = await getDocs(guidesQuery);
    const deletePromises = [];
    
    querySnapshot.forEach((doc) => {
      console.log('Deleting draft:', doc.id);
      deletePromises.push(deleteGuide(doc.id));
    });
    
    if (deletePromises.length > 0) {
      await Promise.all(deletePromises);
      console.log('Deleted', deletePromises.length, 'drafts');
    } else {
      console.log('No drafts to delete');
    }
  } catch (error) {
    console.error("Error deleting all drafts:", error);
    throw error;
  }
}; 